import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import {
    ButtonType,
    CardCatalogueDataSource,
    CardContainer,
    EMPTY_LINK,
    ICardComponent,
    IChip,
    ILink,
    ImageData,
    MediaImage,
    withConfigFallback,
} from '@sirius/common';
import { Product } from '@urweb/snowtype';

import { ButtonCommerce, CardProductCommerce } from 'commerce/models';
import { TrackingCommerceSnowplowService } from 'commerce/services';
import { ProductCardComponent } from 'shared';
import { StoreExperienceTypes } from 'shared/constants';

@Component({
    selector: 'commerce-card-product',
    templateUrl: './card-product-commerce.component.html',
    styleUrls: ['./card-product-commerce.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ProductCardComponent],
})
export class CardProductCommerceComponent implements ICardComponent<CardProductCommerce> {
    private _trackingCommerceService = inject(TrackingCommerceSnowplowService);

    readonly config = input<CardProductCommerce | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    // Public inputs keep their natural API name. Consumers bind `[title]`, `[image]`, etc.
    readonly title = input('');
    readonly brandName = input('');
    readonly teaserHtml = input('');
    readonly image = input<MediaImage | undefined>(undefined);
    readonly button = input<ButtonCommerce | undefined>(undefined);
    readonly link = input<ILink>(EMPTY_LINK);
    readonly tags = input<IChip[]>([]);
    readonly manufacturer = input('');
    readonly attributes = input<IChip[]>([]);
    readonly imageData = input<ImageData | undefined>(undefined);

    // Internal derived signals read by this component's own template only.
    protected readonly resolvedTitle = withConfigFallback<CardProductCommerce, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedBrandName = withConfigFallback<CardProductCommerce, 'brandName', string>(this.config, 'brandName', this.brandName);
    protected readonly resolvedTeaserHtml = withConfigFallback<CardProductCommerce, 'teaserHtml', string>(this.config, 'teaserHtml', this.teaserHtml);
    protected readonly resolvedImage = withConfigFallback<CardProductCommerce, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedButton = withConfigFallback<CardProductCommerce, 'button'>(this.config, 'button', this.button);
    protected readonly resolvedLink = withConfigFallback<CardProductCommerce, 'link', ILink>(this.config, 'link', this.link);
    protected readonly resolvedTags = withConfigFallback<CardProductCommerce, 'tags', IChip[]>(this.config, 'tags', this.tags);
    protected readonly resolvedManufacturer = withConfigFallback<CardProductCommerce, 'manufacturer', string>(this.config, 'manufacturer', this.manufacturer);
    protected readonly resolvedAttributes = withConfigFallback<CardProductCommerce, 'attributes', IChip[]>(this.config, 'attributes', this.attributes);
    protected readonly resolvedImageData = withConfigFallback<CardProductCommerce, 'imageData'>(this.config, 'imageData', this.imageData);

    protected readonly productId = computed(() => this.config()?.productId ?? '');

    readonly selectEvent = output<void>();
    readonly addProduct = output<void>();

    readonly locale = 'en-US';
    readonly buttonType: ButtonType = 'primary';
    readonly storeExperienceTypeEnum = StoreExperienceTypes;

    onClick(): void {
        this.trackProductListClick();
        this.selectEvent.emit();
    }

    private trackProductListClick(): void {
        const config = this.config();
        const product: Product = {
            id: this.productId(),
            name: this.resolvedTitle(),
            brand: this.resolvedManufacturer(),
            creative_id: this.resolvedImage()?.image?.url ?? null,
            compatibility: config?.compatibility ?? [],
            category: config?.category ?? '',
            product_type: config?.product_type ?? [],
            application_type: config?.application_type ?? [],
            position: config?.position ?? null,
        };

        this._trackingCommerceService.trackProductListClick({ product });
    }
}
